import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { FONT_SIZE_PERCENTAGE_DEFAULT, FONT_SIZE_PERCENTAGE_OPTIONS, UIConfiguration } from 'src/models/UIConfiguration'
import { analyticsService } from 'src/services/AnalyticsService'
import { shouldUse24HTimeFormat } from 'src/utils/dates'
import {
    FONT_SIZE_PERCENTAGE_QUERY_PARAM,
    IS_EDITING_LOCKED_QUERY_PARAM,
    IS_FULL_WIDTH_MODE_QUERY_PARAM,
    IS_HEADER_HIDDEN_QUERY_PARAM,
    SHOULD_DISPLAY_LOGO_QUERY_PARAM,
    SHOULD_SHOW_TRANSCRIPT_IN_ALL_CAPS_QUERY_PARAM,
    isChromakeyModeActive,
} from 'src/utils/env'

const LOCAL_STORAGE_KEY = 'verbitext_ui_configuration_v2'

type ConfigSetFn = <T extends keyof UIConfiguration>(
    prop: T,
    value: UIConfiguration[T] | ((oldVal: UIConfiguration[T]) => UIConfiguration[T]),
) => unknown

type ConfigurationContextProps = {
    config: UIConfiguration
    setProp: ConfigSetFn
}

const ConfigurationContext = React.createContext<ConfigurationContextProps | null>(null)

type ConfigurationProviderProps = {
    children: React.ReactNode
}

const analyticsEventsMap: Partial<Record<keyof UIConfiguration, string>> = {
    isTimestampFormatsEnabled: 'Timestamp formats switch toggled',
    isShowSpeakerColorCodeEnabled: 'Show speaker color code switch toggled',
}

const getUiConfigOverrides = (): Partial<UIConfiguration> => {
    const urlParams = new URLSearchParams(window.location.search)
    const overrides: Partial<UIConfiguration> = {}

    const fontSizePercentageParam = parseInt(urlParams.get(FONT_SIZE_PERCENTAGE_QUERY_PARAM) || '')
    if (FONT_SIZE_PERCENTAGE_OPTIONS.includes(fontSizePercentageParam)) {
        overrides.fontSizePercentage = fontSizePercentageParam
    }

    const booleanParams: (keyof UIConfiguration)[] = [
        IS_HEADER_HIDDEN_QUERY_PARAM,
        IS_FULL_WIDTH_MODE_QUERY_PARAM,
        SHOULD_DISPLAY_LOGO_QUERY_PARAM,
        SHOULD_SHOW_TRANSCRIPT_IN_ALL_CAPS_QUERY_PARAM,
        IS_EDITING_LOCKED_QUERY_PARAM,
    ]
    booleanParams.forEach((param) => {
        let paramValue: any
        try {
            paramValue = JSON.parse(urlParams.get(param) || '')
        } catch (e) {}
        if (typeof paramValue === 'boolean') {
            overrides[param] = paramValue as any
        }
    })

    return overrides
}

export const ConfigurationProvider = (props: ConfigurationProviderProps) => {
    const { children } = props
    const [config, setConfig] = useState<UIConfiguration>(() => {
        const defaultConfig: UIConfiguration = {
            fontSizePercentage: FONT_SIZE_PERCENTAGE_DEFAULT,
            isHeaderHidden: false,
            isFullWidthMode: false,
            shouldDisplayLogo: false,
            isTimestampFormatsEnabled: true,
            isShowSpeakerColorCodeEnabled: true,
            shouldShowTranscriptInAllCaps: false,
            isEditingLocked: false,
            timeFormat: shouldUse24HTimeFormat ? '24h' : '12h',
        }

        // access to local storage might be blocked in incognito mode
        try {
            const savedConfig = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY) || '')
            if (savedConfig) {
                Object.assign(defaultConfig, savedConfig)
            }
        } catch (e) {}

        const configOverrides = getUiConfigOverrides()
        Object.assign(defaultConfig, configOverrides)

        return defaultConfig
    })

    const setProp = useCallback<ConfigSetFn>((prop, value) => {
        setConfig((oldConfig) => ({
            ...oldConfig,
            [prop]: sanitise(prop, typeof value === 'function' ? value(oldConfig[prop]) : value),
        }))

        const analyticsEvent = analyticsEventsMap[prop]
        if (analyticsEvent) {
            analyticsService.track(analyticsEvent, { [prop]: value })
        }
    }, [])

    useEffect(() => {
        if (isChromakeyModeActive) {
            return
        }
        try {
            localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(config))
        } catch (e) {}
    }, [config])

    return (
        <ConfigurationContext.Provider value={useMemo(() => ({ config, setProp }), [config, setProp])}>
            {children}
        </ConfigurationContext.Provider>
    )
}

export const useConfig = (): [UIConfiguration, ConfigSetFn] => {
    const ctx = useContext(ConfigurationContext)

    if (!ctx) {
        throw new Error('You forgot to use <ConfigurationProvider />, shame on you!')
    }

    const { config, setProp } = ctx

    return [config, setProp]
}

const sanitise = <T extends keyof UIConfiguration>(prop: T, value: UIConfiguration[T]): UIConfiguration[T] => {
    switch (prop) {
        case 'fontSizePercentage':
            return FONT_SIZE_PERCENTAGE_OPTIONS.includes(value as number) ? value : (FONT_SIZE_PERCENTAGE_DEFAULT as UIConfiguration[T])
        default:
            return value
    }
}
